import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal/Modal';
import Btn from '../Btn/Btn';
import { QuestionnaireSummary } from './types';
import { QuestionnaireKind, isTherapyMaterialKind } from '../../types/QuestionnaireKind';
import './DeleteQuestionnaireModal.css';

type Props = {
    questionnaire: QuestionnaireSummary | null;
    isDeleting: boolean;
    error: string | null;
    onClose: () => void;
    onDelete: () => Promise<void>;
    resourceKind: QuestionnaireKind;
};

const DeleteQuestionnaireModal = ({
    questionnaire,
    isDeleting,
    error,
    onClose,
    onDelete,
    resourceKind,
}: Props): JSX.Element | null => {
    const { t } = useTranslation();
    const isTherapyMaterial = isTherapyMaterialKind(resourceKind);
    const [confirmationName, setConfirmationName] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        // Reset state when modal opens/closes
        if (questionnaire) {
            setConfirmationName('');
            setValidationError(null);
        }
    }, [questionnaire]);

    if (!questionnaire) {
        return null;
    }

    const handleDelete = async (): Promise<void> => {
        // Validate that the entered name matches the questionnaire name
        if (confirmationName.trim() !== questionnaire.displayName.trim()) {
            setValidationError(
                isTherapyMaterial
                    ? t('The therapy material name does not match.')
                    : t('The questionnaire name does not match.'),
            );
            return;
        }

        setValidationError(null);
        await onDelete();
    };

    const isConfirmationValid = confirmationName.trim() === questionnaire.displayName.trim();

    return (
        <Modal title={isTherapyMaterial ? t('Delete Therapy Material') : t('Delete Questionnaire')} close={onClose}>
            <div className="frontpage__delete-questionnaire-form">
                <p>
                    <strong>
                        {isTherapyMaterial
                            ? t('Are you sure you want to delete this therapy material?')
                            : t('Are you sure you want to delete this questionnaire?')}
                    </strong>
                </p>
                <div className="frontpage__warning-box">
                    {!questionnaire.isPrivate && (
                        <p>
                            <strong>
                                {isTherapyMaterial
                                    ? t('This therapy material is shared and will be removed for all therapists.')
                                    : t('This questionnaire is shared and will be removed for all therapists.')}
                            </strong>
                        </p>
                    )}
                    <p>
                        {t(
                            'If it is assigned or has responses, it is archived instead: it disappears from all lists, but existing assignments and responses remain available.',
                        )}
                    </p>
                </div>
                <div className="frontpage__assign-details">
                    <div>
                        <strong>{isTherapyMaterial ? t('Therapy Material') : t('Questionnaire')}</strong>
                    </div>
                    <div>{questionnaire.displayName}</div>
                </div>
                <div className="frontpage__confirmation-section">
                    <p>
                        {isTherapyMaterial
                            ? t('To confirm deletion, please enter the therapy material name below:')
                            : t('To confirm deletion, please enter the questionnaire name below:')}
                    </p>
                    <input
                        type="text"
                        className="frontpage__confirmation-input"
                        placeholder={
                            isTherapyMaterial
                                ? t('Enter therapy material name to confirm')
                                : t('Enter questionnaire name to confirm')
                        }
                        value={confirmationName}
                        onChange={(e) => {
                            setConfirmationName(e.target.value);
                            setValidationError(null);
                        }}
                        disabled={isDeleting}
                        autoFocus
                    />
                </div>
                {validationError && <div className="frontpage__error">{validationError}</div>}
                {error && <div className="frontpage__error">{error}</div>}
                <div className="frontpage__assign-actions">
                    <button
                        type="button"
                        className="regular-btn frontpage__delete-btn"
                        onClick={handleDelete}
                        disabled={isDeleting || !isConfirmationValid}
                    >
                        {isDeleting
                            ? isTherapyMaterial
                                ? t('Deleting therapy material...')
                                : t('Deleting questionnaire...')
                            : t('Delete')}
                    </button>
                    <Btn title={t('Cancel')} variant="secondary" onClick={onClose} disabled={isDeleting} />
                </div>
            </div>
        </Modal>
    );
};

export default DeleteQuestionnaireModal;
